import { useStoreValue } from 'state/hooks/useStore';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useInternalApiRequest } from 'shared/hooks/useInternalApiRequest';
import type { InternalApiRequestError } from 'shared/hooks/useInternalApiRequest/types';
import { formatSubscriptionData, getShopifyContractId } from 'shared/utils/subscriptionV2Helpers';
import { SUBSCRIPTION_API_URL } from '@portal/config/base';
import { useSubscriptionApiErrorToast } from 'shared/hooks';
import { SubscriptionsResponseV2 } from '../getSubscriptions/types';
import { getQueryKeyForGetSubscriptionsV2 } from '../getSubscriptions';
import { queryKeyForSubscriptionDetailsV2 } from '../getSubscriptionDetails';
import { SubscriptionDetailsV2Response } from '../getSubscriptionDetails/types';
import { PutSubscriptionDetailsV2InitialResponse, PutSubscriptionDetailsV2Request } from './types';

export const useMutationUpdateSubscriptionDetailsV2 = () => {
  const queryClient = useQueryClient();
  const request = useInternalApiRequest();
  const store = useStoreValue();
  const displaySubscriptionApiErrorToast = useSubscriptionApiErrorToast();

  return useMutation<
    PutSubscriptionDetailsV2InitialResponse,
    InternalApiRequestError,
    PutSubscriptionDetailsV2Request
  >({
    mutationFn: ({ contractId, data }: PutSubscriptionDetailsV2Request) =>
      request<PutSubscriptionDetailsV2InitialResponse>({
        method: 'PUT',
        url: `${SUBSCRIPTION_API_URL[store]}/user/subscription/${contractId}`,
        data
      }),
    onSuccess: (response, { contractId, isReactivate = false, onSuccess }) => {
      if (onSuccess) onSuccess();

      // update the cached subscription details data
      queryClient.setQueryData(
        queryKeyForSubscriptionDetailsV2(store, contractId),
        (subscriptionDetails: SubscriptionDetailsV2Response) =>
          subscriptionDetails && {
            ...subscriptionDetails,
            ...formatSubscriptionData(response),
            ...(isReactivate && {
              is_active: true,
              status: 'ACTIVE'
            })
          }
      );

      // update the cached subscription list data
      queryClient.setQueryData(
        getQueryKeyForGetSubscriptionsV2(store),
        (subscriptionList: SubscriptionsResponseV2) => {
          // only update the list if it has already been cached
          if (subscriptionList) {
            const responseId = getShopifyContractId(response.shopify_contract_id);
            const updatedList = subscriptionList.map((subscription) =>
              getShopifyContractId(subscription.shopify_contract_id) === responseId
                ? {
                    ...subscription,
                    ...formatSubscriptionData(response),
                    ...(isReactivate && {
                      is_active: true,
                      status: 'ACTIVE'
                    })
                  }
                : subscription
            );

            return updatedList;
          }

          return [];
        }
      );
    },
    onError(error) {
      displaySubscriptionApiErrorToast(error);
    }
  });
};
